import { readStoredValue, removeStoredValue, writeStoredValue } from './storage';

export type ConsentStatus = 'granted' | 'denied';

export type ConsentState = {
  analyticsStorage: ConsentStatus;
  adStorage: ConsentStatus;
  adUserData: ConsentStatus;
  adPersonalization: ConsentStatus;
  updatedAt: string;
  version: number;
};

type StoredConsentState = Partial<ConsentState>;

type GtagConsentPayload = {
  analytics_storage: ConsentStatus;
  ad_storage: ConsentStatus;
  ad_user_data: ConsentStatus;
  ad_personalization: ConsentStatus;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export const consentStorageKey = 'agentteams-consent';
export const consentChangedEventName = 'agentteams-consent-changed';

const consentVersion = 1;

const isBrowser = () => typeof window !== 'undefined';

const isConsentStatus = (value: unknown): value is ConsentStatus => {
  return value === 'granted' || value === 'denied';
};

const toGtagConsentPayload = (consent: ConsentState): GtagConsentPayload => {
  return {
    analytics_storage: consent.analyticsStorage,
    ad_storage: consent.adStorage,
    ad_user_data: consent.adUserData,
    ad_personalization: consent.adPersonalization,
  };
};

const notifyConsentChanged = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(consentChangedEventName));
};

const getDefaultDeniedConsent = (): ConsentState => {
  return {
    analyticsStorage: 'denied',
    adStorage: 'denied',
    adUserData: 'denied',
    adPersonalization: 'denied',
    updatedAt: new Date().toISOString(),
    version: consentVersion,
  };
};

const isValidConsentState = (value: StoredConsentState): value is ConsentState => {
  return (
    isConsentStatus(value.analyticsStorage) &&
    isConsentStatus(value.adStorage) &&
    isConsentStatus(value.adUserData) &&
    isConsentStatus(value.adPersonalization) &&
    typeof value.updatedAt === 'string' &&
    typeof value.version === 'number'
  );
};

// 저장소 차단 환경에서 저장이 실패해도 같은 탭 동안은 배너가 다시 뜨지 않게
// 메모리에 마지막 기록을 유지한다. 다른 탭에서 지운 값과 구분하기 위해,
// 메모리 폴백은 '마지막 저장이 실패한 경우'에만 쓴다. 저장이 성공한 뒤 저장소가
// 비면(다른 탭의 clear 등) 저장소를 truth로 보고 null을 반환한다.
let memoryConsent: ConsentState | null = null;
let lastPersistSucceeded = true;

export const getConsent = (): ConsentState | null => {
  if (!isBrowser()) return null;

  const memoryFallback = lastPersistSucceeded ? null : memoryConsent;
  // 저장소 차단 환경에서는 동의 기록 없음으로 취급한다 (WEB-R).
  const raw = readStoredValue(consentStorageKey);
  if (!raw) return memoryFallback;

  try {
    const parsed = JSON.parse(raw) as StoredConsentState;
    if (!isValidConsentState(parsed)) return memoryFallback;
    return parsed;
  } catch {
    return memoryFallback;
  }
};

/**
 * 동의를 기록하고 저장 성공 여부를 반환한다.
 * 저장소가 차단된 환경에서는 같은 탭 동안 재노출만 막고(메모리 유지),
 * `false`를 반환해 호출부가 '이 브라우저에서는 기억되지 않음'을 알 수 있게 한다.
 */
export const saveConsent = (consent: Omit<ConsentState, 'updatedAt' | 'version'>): boolean => {
  const nextConsent: ConsentState = {
    ...consent,
    updatedAt: new Date().toISOString(),
    version: consentVersion,
  };
  memoryConsent = nextConsent;
  const persisted = writeStoredValue(consentStorageKey, JSON.stringify(nextConsent));
  lastPersistSucceeded = persisted;
  notifyConsentChanged();
  return persisted;
};

export const clearConsent = () => {
  if (!isBrowser()) return;
  memoryConsent = null;
  removeStoredValue(consentStorageKey);
  notifyConsentChanged();
};

export const applyConsentUpdate = (consent: ConsentState | null) => {
  if (!isBrowser()) return;
  if (!window.gtag) return;

  const effectiveConsent = consent ?? getDefaultDeniedConsent();
  window.gtag('consent', 'update', toGtagConsentPayload(effectiveConsent));
};

export const buildAcceptAllConsent = (): Omit<ConsentState, 'updatedAt' | 'version'> => {
  return {
    analyticsStorage: 'granted',
    adStorage: 'granted',
    adUserData: 'granted',
    adPersonalization: 'granted',
  };
};

export const buildRejectAllConsent = (): Omit<ConsentState, 'updatedAt' | 'version'> => {
  return {
    analyticsStorage: 'denied',
    adStorage: 'denied',
    adUserData: 'denied',
    adPersonalization: 'denied',
  };
};
